import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchItems, fetchItemsByIds } from '../services/smr-admin';
import type { ItemListParams, ItemsPage } from '../services/smr-admin';

const STALE = 10 * 60 * 1000;
const GC    = 30 * 60 * 1000;

const clean = (ids: (string | null | undefined)[]) =>
    Array.from(new Set(ids.filter((id): id is string => Boolean(id)))).sort();

export const itemKeys = {
    all: ['smr-admin', 'items'] as const,
    lists: () => [...itemKeys.all, 'list'] as const,
    list: (params: ItemListParams) => [...itemKeys.lists(), params] as const,
    infiniteList: (params: ItemListParams) => [...itemKeys.lists(), 'infinite', params] as const,
    detail: (id: string) => [...itemKeys.all, 'detail', id] as const,
    byIds: (ids: string[]) => [...itemKeys.all, 'byIds', ids] as const,
};

export const useItemsQuery = (params: ItemListParams = {}, opts?: { suppressForbiddenToast?: boolean }) =>
    useQuery({
        queryKey: itemKeys.list(params),
        queryFn: () => fetchItems(params, opts),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

export const useInfiniteItemsQuery = (params: ItemListParams, opts?: { suppressForbiddenToast?: boolean }) =>
    useInfiniteQuery({
        queryKey: itemKeys.infiniteList(params),
        queryFn: ({ pageParam }) => fetchItems({ ...params, page: pageParam }, opts),
        initialPageParam: 1,
        getNextPageParam: ({ meta }: ItemsPage) =>
            meta.current_page < meta.last_page ? meta.current_page + 1 : undefined,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

export const useItemsByIds = (ids: (string | null | undefined)[]) => {
    const unique = clean(ids);
    return useQuery({
        queryKey: itemKeys.byIds(unique),
        queryFn: () => fetchItemsByIds(unique),
        enabled: unique.length > 0,
        staleTime: STALE,
        gcTime: GC,
    });
};
